// PDF Translation utility
// Invokes the pdfTranslate Lambda which uses Textract + Translate + DOCX generation.
// The PDF must already be uploaded to S3 before calling this.

import { fetchAuthSession } from "@aws-amplify/auth";
import { LambdaClient, InvokeCommand } from "@aws-sdk/client-lambda";

const cfnOutputs = require("../../../cfnOutputs.json");

interface TranslatePdfParams {
	bucket: string;
	inputKey: string;
	outputKey: string;
	sourceLanguage: string;
	targetLanguage: string;
	fileName: string;
}

interface TranslatePdfResult {
	bucket: string;
	outputKey: string;
	elementsProcessed: number;
	tablesFound: number;
}

export async function translatePdf(
	params: TranslatePdfParams,
): Promise<TranslatePdfResult> {
	const { credentials } = await fetchAuthSession();
	if (!credentials) {
		throw new Error("No credentials available");
	}

	const region = cfnOutputs.awsRegion;
	const functionName = cfnOutputs.pdfTranslateFunctionName;

	if (!functionName) {
		throw new Error(
			"PDF translation is not configured. Missing pdfTranslateFunctionName in outputs.",
		);
	}

	const client = new LambdaClient({
		region,
		credentials: {
			accessKeyId: credentials.accessKeyId,
			secretAccessKey: credentials.secretAccessKey,
			sessionToken: credentials.sessionToken,
		},
	});

	const response = await client.send(
		new InvokeCommand({
			FunctionName: functionName,
			Payload: JSON.stringify(params),
		}),
	);

	if (response.FunctionError) {
		const errorPayload = JSON.parse(
			new TextDecoder().decode(response.Payload),
		);
		throw new Error(
			`PDF translation failed: ${errorPayload.errorMessage || errorPayload.errorType || "Unknown error"}`,
		);
	}

	const result = JSON.parse(new TextDecoder().decode(response.Payload));
	return result as TranslatePdfResult;
}
